import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { Send, CheckCircle2, ArrowRight } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

interface ProjectReserveDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  projectName: string;
  referenceCode?: string;
  assignedAgentId?: string | null;
}

interface UnitType {
  id: string;
  name: string;
  bedrooms: number | null;
  area_min: number | null;
  area_max: number | null;
  price_min: number | null;
  price_max: number | null;
  currency: string | null;
}

const schema = z.object({
  name: z.string().trim().min(1, 'Name required').max(100),
  phone: z.string().trim().min(7, 'Valid phone required').max(20),
  email: z.string().trim().email('Invalid email').max(255).optional().or(z.literal('')),
  message: z.string().trim().min(1).max(1000),
  unit_type_id: z.string().min(1, 'Please select a unit'),
});

const buildMessage = (project: string, ref: string, unit: string) =>
  `I'd like to reserve a unit at ${project} (${ref}). Selected unit: ${unit}. Please contact me with availability and next steps.`;

const formatUnitLabel = (u: UnitType) =>
  `${u.name}${u.bedrooms ? ` · ${u.bedrooms}BR` : ''}${u.area_min ? ` · ${u.area_min}${u.area_max && u.area_max !== u.area_min ? `–${u.area_max}` : ''} m²` : ''}${u.price_min ? ` · from ${u.currency || 'ETB'} ${u.price_min.toLocaleString()}` : ''}`;

export function ProjectReserveDialog({
  open, onOpenChange, projectId, projectName, referenceCode, assignedAgentId,
}: ProjectReserveDialogProps) {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const refId = (referenceCode || `ID${projectId.slice(0, 8)}`).toUpperCase();

  const [units, setUnits] = useState<UnitType[]>([]);
  const [unitId, setUnitId] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [msgEdited, setMsgEdited] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [step, setStep] = useState<'form' | 'confirm'>('form');
  const [confirmedUnitLabel, setConfirmedUnitLabel] = useState('');
  const [createdLeadId, setCreatedLeadId] = useState<string | null>(null);
  const [submittedAt, setSubmittedAt] = useState<Date | null>(null);

  const selectedUnit = useMemo(() => units.find(u => u.id === unitId), [units, unitId]);

  useEffect(() => {
    if (!open) return;
    setName(profile?.full_name || profile?.name || '');
    setPhone(profile?.phone || '');
    setEmail(user?.email || '');
    setMsgEdited(false);
    setStep('form');
    setCreatedLeadId(null);
    setConfirmedUnitLabel('');
    setSubmittedAt(null);
  }, [open, profile, user]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      const { data } = await supabase
        .from('project_unit_types')
        .select('id, name, bedrooms, area_min, area_max, price_min, price_max, currency')
        .eq('project_id', projectId)
        .order('sort_order', { ascending: true });
      setUnits(data || []);
    })();
  }, [open, projectId]);

  useEffect(() => {
    if (msgEdited) return;
    setMessage(buildMessage(projectName, refId, selectedUnit?.name || '—'));
  }, [selectedUnit, projectName, refId, msgEdited]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = schema.safeParse({ name, phone, email, message, unit_type_id: unitId });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message || 'Please fill required fields');
      return;
    }
    setSubmitting(true);
    try {
      const finalMessage = `${parsed.data.message}\n\n[Unit: ${selectedUnit?.name} | Project Ref: ${refId}]`;
      const { data: lead, error } = await supabase
        .from('leads')
        .insert({
          name: parsed.data.name,
          phone: parsed.data.phone,
          email: parsed.data.email || null,
          message: finalMessage,
          project_id: projectId,
          source: 'project_reserve',
          agent_id: assignedAgentId || null,
          assigned_to: assignedAgentId || null,
          sender_id: user?.id ?? null,
          priority: 'high',
        })
        .select('id')
        .single();
      if (error) throw error;

      setCreatedLeadId(lead?.id ?? null);
      setConfirmedUnitLabel(selectedUnit ? formatUnitLabel(selectedUnit) : '—');
      setSubmittedAt(new Date());
      setStep('confirm');
      toast.success('Reservation request sent!');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to send. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const goToDashboard = () => {
    onOpenChange(false);
    if (user) {
      navigate('/dashboard');
    } else {
      navigate('/login?next=/dashboard');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{step === 'confirm' ? 'Reservation Sent' : 'Reserve a Unit'}</DialogTitle>
          <DialogDescription className="flex items-center gap-2 text-xs">
            <span className="truncate">{projectName}</span>
            <Badge variant="outline" className="font-mono text-[10px] shrink-0">{refId}</Badge>
          </DialogDescription>
        </DialogHeader>

        {step === 'confirm' ? (
          <div className="space-y-4">
            <div className="flex flex-col items-center text-center py-2">
              <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center mb-3">
                <CheckCircle2 className="h-8 w-8 text-primary" />
              </div>
              <h3 className="font-bold text-lg">Thank you, {name.split(' ')[0] || 'there'}!</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Our agent will contact you shortly to confirm availability.
              </p>
            </div>

            <div className="border rounded-lg p-3 space-y-2 bg-muted/30">
              <div className="flex justify-between gap-2 text-xs">
                <span className="text-muted-foreground">Project</span>
                <span className="font-medium text-right">{projectName}</span>
              </div>
              <div className="flex justify-between gap-2 text-xs">
                <span className="text-muted-foreground">Reference</span>
                <span className="font-mono">{refId}</span>
              </div>
              <div className="flex justify-between gap-2 text-xs">
                <span className="text-muted-foreground">Selected unit</span>
                <span className="font-medium text-right">{confirmedUnitLabel}</span>
              </div>
              {submittedAt && (
                <div className="flex justify-between gap-2 text-xs">
                  <span className="text-muted-foreground">Submitted</span>
                  <span>{submittedAt.toLocaleString()}</span>
                </div>
              )}
              {createdLeadId && (
                <div className="flex justify-between gap-2 text-xs">
                  <span className="text-muted-foreground">Lead ID</span>
                  <span className="font-mono">{createdLeadId.slice(0, 8).toUpperCase()}</span>
                </div>
              )}
            </div>

            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)}>
                Close
              </Button>
              <Button className="flex-1 gap-1.5" onClick={goToDashboard}>
                {user ? 'View in Dashboard' : 'Sign in to track'}
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ) : (
          <>
            {!user && (
              <div className="text-xs bg-primary/5 border border-primary/20 text-primary rounded px-3 py-2">
                Sign in for faster reservations — your details will auto-fill.
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <Label className="text-xs font-medium mb-1 block">Available Unit <span className="text-destructive">*</span></Label>
                <Select value={unitId} onValueChange={setUnitId}>
                  <SelectTrigger><SelectValue placeholder={units.length ? 'Select a unit' : 'No units available'} /></SelectTrigger>
                  <SelectContent>
                    {units.map(u => (
                      <SelectItem key={u.id} value={u.id}>{formatUnitLabel(u)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs font-medium mb-1 block">Name <span className="text-destructive">*</span></Label>
                <Input value={name} onChange={e => setName(e.target.value)} required />
              </div>

              <div>
                <Label className="text-xs font-medium mb-1 block">Phone <span className="text-destructive">*</span></Label>
                <div className="flex gap-2">
                  <div className="flex items-center gap-1 border rounded-md px-2 text-sm text-muted-foreground shrink-0">🇪🇹 +251</div>
                  <Input value={phone} onChange={e => setPhone(e.target.value)} required />
                </div>
              </div>

              <div>
                <Label className="text-xs font-medium mb-1 block">Email</Label>
                <Input type="email" value={email} onChange={e => setEmail(e.target.value)} />
              </div>

              <div>
                <Label className="text-xs font-medium mb-1 block">Message</Label>
                <Textarea
                  rows={4}
                  value={message}
                  onChange={e => { setMessage(e.target.value); setMsgEdited(true); }}
                />
              </div>

              <Button type="submit" className="w-full h-11 font-bold gap-2" disabled={submitting}>
                <Send className="h-4 w-4" />
                {submitting ? 'Sending...' : 'Send Message'}
              </Button>
              <p className="text-[10px] text-muted-foreground text-center">
                By submitting, you agree to our Terms of Use.
              </p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
